import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  IconButton,
  Card,
  CardContent,
  Typography,
  Stack,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip // ДОДАЄМО ЦЕЙ ІМПОРТ
} from '@mui/material';
import {
  CameraAlt,
  Delete,
  Visibility,
  PhotoCamera,
  PhotoLibrary
} from '@mui/icons-material';
import { useI18n } from '../i18n/i18n';

const PhotoCapture = ({ onPhotosChange, existingPhotos = [] }) => {
  const { t } = useI18n();
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const [photos, setPhotos] = useState(existingPhotos);
  const [error, setError] = useState('');
  const [cameraDialogOpen, setCameraDialogOpen] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);

  // Вибір фото з галереї
  const handleSelectFromGallery = () => {
    fileInputRef.current?.click();
  };

  // Запуск камери
  const handleStartCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'environment' } 
      });
      setCameraStream(stream);
      setCameraDialogOpen(true);
    } catch (err) {
      setError(t('camera_access_denied'));
      console.error('Camera access denied:', err);
    }
  };

  // Знімок з камери
  const handleTakePicture = () => {
    if (cameraInputRef.current) {
      const video = cameraInputRef.current;
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const context = canvas.getContext('2d');
      context.drawImage(video, 0, 0);
      
      canvas.toBlob((blob) => {
        const newPhoto = {
          id: Date.now() + Math.random(),
          file: new File([blob], `camera_${Date.now()}.jpg`, { type: 'image/jpeg' }),
          url: URL.createObjectURL(blob),
          timestamp: new Date().toISOString(),
          description: '',
          source: 'camera'
        };

        const updatedPhotos = [...photos, newPhoto];
        setPhotos(updatedPhotos);
        onPhotosChange(updatedPhotos);
        setCameraDialogOpen(false);
        stopCamera();
      }, 'image/jpeg', 0.8);
    }
  };

  // Зупинка камери
  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
  };

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    const validFiles = files.filter(file => 
      file.type.startsWith('image/') && file.size <= 5 * 1024 * 1024
    );

    if (validFiles.length !== files.length) {
      setError(t('photo_invalid_files'));
    }

    if (validFiles.length > 0) {
      const newPhotos = validFiles.map(file => ({
        id: Date.now() + Math.random(),
        file,
        url: URL.createObjectURL(file),
        timestamp: new Date().toISOString(),
        description: '',
        source: 'gallery'
      }));

      const updatedPhotos = [...photos, ...newPhotos];
      setPhotos(updatedPhotos);
      onPhotosChange(updatedPhotos);
      setError('');
    }

    event.target.value = '';
  };

  const removePhoto = (photoId) => {
    const updatedPhotos = photos.filter(photo => photo.id !== photoId);
    setPhotos(updatedPhotos);
    onPhotosChange(updatedPhotos);
  };

  const updateDescription = (photoId, description) => {
    const updatedPhotos = photos.map(photo =>
      photo.id === photoId ? { ...photo, description } : photo
    );
    setPhotos(updatedPhotos);
    onPhotosChange(updatedPhotos);
  };

  // Закриття діалогу камери
  const handleCloseCamera = () => {
    setCameraDialogOpen(false);
    stopCamera();
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {t('technician_photos')}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Кнопки додавання фото */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
          <Button
            variant="contained"
            startIcon={<PhotoCamera />}
            onClick={handleStartCamera}
            fullWidth
          >
            {t('take_photo')}
          </Button>
          <Button
            variant="outlined"
            startIcon={<PhotoLibrary />}
            onClick={handleSelectFromGallery}
            fullWidth
          >
            {t('choose_from_gallery')}
          </Button>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            accept="image/*"
            multiple
            style={{ display: 'none' }}
          />
        </Stack>

        {/* Галерея фото */}
        <Stack spacing={2}>
          {photos.map((photo) => (
            <Card key={photo.id} variant="outlined">
              <CardContent>
                <Stack direction="row" spacing={2} alignItems="flex-start">
                  {/* Прев'ю фото */}
                  <Box
                    sx={{
                      width: 100,
                      height: 100,
                      borderRadius: 1,
                      overflow: 'hidden',
                      flexShrink: 0,
                      position: 'relative'
                    }}
                  >
                    <img
                      src={photo.url}
                      alt={photo.description}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                    />
                    <Chip
                      label={photo.source === 'camera' ? t('camera') : t('gallery')}
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: 4,
                        left: 4,
                        backgroundColor: 'rgba(0,0,0,0.7)',
                        color: 'white',
                        fontSize: '0.6rem'
                      }}
                    />
                  </Box>

                  {/* Інформація про фото */}
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      {new Date(photo.timestamp).toLocaleString()}
                    </Typography>
                    
                    {/* Опис фото */}
                    <textarea
                      placeholder={t('photo_description_placeholder')}
                      value={photo.description}
                      onChange={(e) => updateDescription(photo.id, e.target.value)}
                      style={{
                        width: '100%',
                        minHeight: 60,
                        marginTop: 8,
                        padding: 8,
                        border: '1px solid #ccc',
                        borderRadius: 4,
                        fontSize: '14px',
                        resize: 'vertical'
                      }}
                    />
                  </Box>

                  {/* Кнопки управління */}
                  <Stack spacing={1}>
                    <IconButton
                      size="small"
                      onClick={() => window.open(photo.url, '_blank')}
                      title={t('view_photo')}
                    >
                      <Visibility />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => removePhoto(photo.id)}
                      title={t('delete_photo')}
                    >
                      <Delete />
                    </IconButton>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>

        {photos.length === 0 && (
          <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ py: 3 }}>
            {t('no_photos_added')}
          </Typography>
        )}

        {/* Діалог камери */}
        <Dialog 
          open={cameraDialogOpen} 
          onClose={handleCloseCamera}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            {t('take_photo')}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
              <video
                ref={cameraInputRef}
                autoPlay
                playsInline
                style={{
                  width: '100%',
                  maxWidth: 500,
                  borderRadius: 8,
                  backgroundColor: '#000'
                }}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseCamera}>
              {t('cancel')}
            </Button>
            <Button 
              variant="contained" 
              onClick={handleTakePicture}
              startIcon={<CameraAlt />}
            >
              {t('capture')}
            </Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default PhotoCapture;